import { VerifyUserStatus } from 'helpers/api_method';
import { toast } from 'react-toastify';

export interface UserStatusResponse {
  status: 'active' | 'inactive' | 'error';
  reason?: 'user_not_found' | 'account_inactive';
  user_status?: string;
  message?: string;
  timestamp?: string;
}

class UserStatusService {
  private static instance: UserStatusService;
  private checkInterval: NodeJS.Timeout | null = null;
  private isChecking = false;
  private lastCheck = 0;
  private readonly CHECK_INTERVAL = 60000; // 60 seconds (reduces server load with 30+ members)
  private readonly MIN_CHECK_INTERVAL = 15000; // 15 seconds minimum

  private constructor() {}

  public static getInstance(): UserStatusService {
    if (!UserStatusService.instance) {
      UserStatusService.instance = new UserStatusService();
    }
    return UserStatusService.instance;
  }

  /**
   * Start monitoring user status (runs once on load; no periodic polling - reload for latest status)
   */
  public startMonitoring(): void {
    if (this.checkInterval) {
      this.stopMonitoring();
    }
    this.checkUserStatus();
  }

  /**
   * Stop monitoring user status
   */
  public stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Check user status immediately
   */
  public async checkUserStatus(): Promise<UserStatusResponse | null> {
    const now = Date.now();
    
    // Prevent too frequent checks
    if (this.isChecking || (now - this.lastCheck) < this.MIN_CHECK_INTERVAL) {
      return null;
    }

    this.isChecking = true;
    this.lastCheck = now;

    try {
      const response = await VerifyUserStatus();
      // Backend returns 200 with structured payload
      const statusData = (response?.data || response) as UserStatusResponse;

      if (statusData && statusData.status === 'inactive') {
        this.handleInactiveUser(statusData);
      }

      return statusData;
    } catch (error: any) {
      console.error('Error checking user status:', error);
      // Fall back: if backend still sends 403/404 in some cases, handle similarly
      const statusData = error?.response?.data as UserStatusResponse;
      if (statusData && (error?.response?.status === 403 || error?.response?.status === 404)) {
        this.handleInactiveUser(statusData);
        return statusData;
      }
    } finally {
      this.isChecking = false;
    }

    return null;
  }

  /**
   * Handle inactive user - force logout
   */
  public handleInactiveUser(statusData: UserStatusResponse): void {
    console.warn('User account is inactive:', statusData);
    
    // Show notification
    const message = statusData.message || 'Your account has been deactivated. You will be logged out.';
    toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    // Clear local storage
    localStorage.removeItem('authUser');
    localStorage.removeItem('authUserRefreshToken');
    localStorage.removeItem('userDetails');
    
    // Clear any other auth-related data
    sessionStorage.clear();
    
    // Force page reload to clear all state
    setTimeout(() => {
      window.location.href = '/login';
    }, 2000);
  }

  /**
   * Force logout user
   */
  public forceLogout(reason: string = 'Session expired'): void {
    toast.error(reason, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    // Clear all auth data
    localStorage.removeItem('authUser');
    localStorage.removeItem('authUserRefreshToken');
    localStorage.removeItem('userDetails');
    sessionStorage.clear();
    
    // Redirect to login
    setTimeout(() => {
      window.location.href = '/login';
    }, 1500);
  }

  /**
   * Check if user is currently being monitored
   */
  public isMonitoring(): boolean {
    return this.checkInterval !== null;
  }


}

export default UserStatusService;
